import { useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import * as Sentry from '@sentry/capacitor';
import { useTranslation } from 'react-i18next';
import type { TimelinePlayback } from 'features/mapping/mapping.types';
import { useTimeRangePlayback } from 'features/mapping/hooks/useTimeRangePlayback';
import { useLightningTimelineLookbackHours } from 'features/mapping/hooks/useLightningTimelineLookbackHours';
import {
  LIGHTNING_TIMELINE_FPS,
  LIGHTNING_TIMELINE_STEP_MIN,
  LIGHTNING_TIMELINE_WINDOW_SEC,
} from './LightningLayer.constants';
import { useLightningSourceLoaded } from './useLightningSourceLoaded';

type UseLightningTimelineOptions = {
  visible: boolean;
  onError?: () => void;
};

export const useLightningTimeline = ({
  visible,
  onError,
}: UseLightningTimelineOptions): TimelinePlayback => {
  const { t } = useTranslation();
  const lookbackMin = useLightningTimelineLookbackHours() * 60;
  // Offset by one window so the first frame's window is within the fetched data
  const startOffsetMin = -lookbackMin + LIGHTNING_TIMELINE_WINDOW_SEC / 60;
  const playback = useTimeRangePlayback({
    visible,
    startOffsetMin,
    endOffsetMin: 0,
    stepMin: LIGHTNING_TIMELINE_STEP_MIN,
    loopSec:
      -startOffsetMin / LIGHTNING_TIMELINE_STEP_MIN / LIGHTNING_TIMELINE_FPS,
  });

  const handleError = useCallback(
    (error: Error): void => {
      Sentry.captureException(error, {
        extra: { context: 'lightning-timeline-error' },
      });
      enqueueSnackbar(t('map.layers.lightning.loadError'), {
        variant: 'error',
      });
      onError?.();
    },
    [onError, t],
  );

  // Loading until the whole lookback is in, so the first loop isn't partial
  const isSourceLoaded = useLightningSourceLoaded(visible, handleError);

  return {
    ...playback,
    isLoading: playback.isLoading || !isSourceLoaded,
  };
};
